import { z } from 'zod';

/**
 * Email field: normalize first (trim + lowercase), then validate RFC format.
 * Used at every API boundary so the DB never stores mixed-case duplicates and
 * logins/password resets are case-insensitive without changing the @unique
 * column behavior. Order matters: `.email()` runs after `.toLowerCase()` so
 * surrounding whitespace and weird casing don't trip the regex.
 */
export const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.string().email('Email invalido'));

/**
 * Login request body.
 */
export const LoginSchema = z.object({
  email: emailField,
  password: z.string().min(1, 'Password requerido'),
});

export type LoginInput = z.infer<typeof LoginSchema>;

/**
 * Login response (JWT payload).
 */
export const JwtPayloadSchema = z.object({
  id: z.number(),
  rut: z.string(),
  email: z.string().email(),
  nombre: z.string(),
  tipoUsuarioId: z.number(),
  ambitoIds: z.array(z.number()),
});

export type JwtPayload = z.infer<typeof JwtPayloadSchema>;

/**
 * Turno (shift) enum: A o B. Semanas alternantes Mié→Mar.
 * Admins/personal de oficina = null (sin turno).
 */
export const TurnoEnum = z.enum(['A', 'B']);
export type Turno = z.infer<typeof TurnoEnum>;

/**
 * Shape of the `ambitos` relation returned by GET /users/:id (Prisma include
 * select).
 */
export const UserAmbitoLinkSchema = z.object({
  ambitoId: z.number(),
  ambito: z.object({
    id: z.number(),
    nombre: z.string(),
    codigo: z.string(),
  }),
});

export type UserAmbitoLink = z.infer<typeof UserAmbitoLinkSchema>;

/**
 * User profile schema (public info).
 */
export const UserProfileSchema = z.object({
  id: z.number(),
  rut: z.string(),
  nombre: z.string(),
  nombres: z.string().nullable(),
  apaterno: z.string().nullable(),
  amaterno: z.string().nullable(),
  email: z.string().email(),
  tipoUsuarioId: z.number(),
  celular: z.string().nullable(),
  turno: TurnoEnum.nullable().optional(),
  activo: z.boolean(),
  ambitos: z.array(UserAmbitoLinkSchema).optional(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

/**
 * Create user request body.
 */

export const CreateUserSchema = z.object({
  rut: z.string().min(1, 'RUT requerido'),
  nombre: z.string().min(1, 'Nombre requerido'),
  nombres: z.string().nullable().optional(),
  apaterno: z.string().nullable().optional(),
  amaterno: z.string().nullable().optional(),
  email: emailField,
  password: z.string().min(8, 'Minimo 8 caracteres'),
  tipoUsuarioId: z.number().int().positive(),
  celular: z.string().nullable().optional(),
  turno: TurnoEnum.nullable().optional(),
  ambitoIds: z.array(z.number().int().positive()).min(1, 'Al menos un ambito requerido'),
});

export type CreateUserInput = z.infer<typeof CreateUserSchema>;

/**
 * Update user request body (password excluded, all fields optional, plus activo toggle for reactivation).
 */
export const UpdateUserSchema = CreateUserSchema
  .omit({ password: true })
  .partial()
  .extend({ activo: z.boolean().optional() });

export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

/**
 * User list filter query parameters.
 */
export const UserListFilterSchema = z.object({
  tipoUsuarioId: z.coerce.number().int().positive().optional(),
  ambitoId: z.coerce.number().int().positive().optional(),
  activo: z.preprocess(
    (val) => (val === 'true' ? true : val === 'false' ? false : val),
    z.boolean().optional(),
  ),
  turno: TurnoEnum.optional(),
  search: z.string().optional(),
});

export type UserListFilter = z.infer<typeof UserListFilterSchema>;
